import { useState } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import CopyToClipboardButton from '@rossum/components/copy-to-clipboard/CopyToClipboardButton';
import { TabPanel } from '@rossum/components/tab-panel/TabPanel';
import {
  Box,
  Divider,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@rossum/ui/material';
import { GridRowParams } from '@rossum/ui/x-data-grid-pro';
import JsonEditor from '../../../../../../components/UI/Editor/components/JsonEditor';
import { camelToSnake } from '../../../../../../lib/keyConvertor';
import {
  formatToJSONString,
  resolveLogValue,
  TransformedActivity,
} from '../../helpers';
import { Logs, LOGS_HEIGHT } from '../logs/Logs';
import { Message } from './Message';

const COMMON_TABS = ['request', 'response', 'output', 'metadata'] as const;
const DETAIL_TABS = ['log' as const, ...COMMON_TABS];

type DetailTab = (typeof DETAIL_TABS)[number];

export const PanelContent = ({ row }: GridRowParams<TransformedActivity>) => {
  const isRunJob = row.hookType === 'job';

  const resolvedTabs = isRunJob ? DETAIL_TABS : COMMON_TABS;

  const [currentTab, setCurrentTab] = useState<DetailTab>(resolvedTabs[0]);

  const handleChange = (newTab: DetailTab) => setCurrentTab(newTab);

  const { message, request, response, output } = row;

  const snakeCasedLog = R.mapKeys(row, key => camelToSnake(key));

  const fixedOutput = output
    ? output.replace(/'/g, '"').replace(/\bNone\b/g, 'null')
    : null;

  const detail = {
    request: resolveLogValue(request),
    response: resolveLogValue(response),
    output: resolveLogValue(fixedOutput),
    metadata: { value: formatToJSONString(snakeCasedLog) },
  };

  const intl = useIntl();

  return (
    <Stack spacing={0.5} p={2}>
      <Message message={message} />
      <Stack spacing={1}>
        <Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Tabs
              value={currentTab}
              onChange={(_, value: DetailTab) => handleChange(value)}
            >
              {resolvedTabs.map(tab => {
                return (
                  <Tab
                    key={tab}
                    value={tab}
                    label={
                      <Typography variant="body2" fontWeight={600}>
                        {intl.formatMessage({
                          id: `features.activities.detail.${tab}.tab`,
                        })}
                      </Typography>
                    }
                  />
                );
              })}
            </Tabs>
          </Stack>
          <Divider />
        </Stack>
        <Stack component={Paper}>
          {currentTab === 'log' ? (
            <Logs runUuid={row.uuid} status={row.status} />
          ) : (
            <>
              {COMMON_TABS.map(tab => {
                const { value } = detail[tab];

                const missingDataMessage =
                  tab === 'metadata'
                    ? ''
                    : intl.formatMessage({
                        id: `features.activities.detail.${tab}.empty`,
                      });

                return (
                  <TabPanel active={currentTab === tab} tabId={tab} key={tab}>
                    <JsonEditor
                      name={tab}
                      value={value === null ? missingDataMessage : value}
                      debounceChangePeriod={0}
                      height={LOGS_HEIGHT}
                      readonly
                      mode={tab === 'output' ? 'plain_text' : 'json'}
                    />
                    {value && (
                      <Box sx={{ position: 'absolute', right: 24, bottom: 24 }}>
                        <CopyToClipboardButton
                          content={value}
                          iconColor="action"
                        />
                      </Box>
                    )}
                  </TabPanel>
                );
              })}
            </>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
